/**
 * HTTP-backed UGC API implementation for PlaygroundHost
 *
 * All operations call Cloud Run directly via HTTP with Firebase Auth token.
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (ugc/index.ts).
 * This ensures Firebase is not bundled in production builds.
 */
import {
  UgcApi,
  UgcEntry,
  UgcCreateParams,
  UgcUpdateParams,
  UgcListParams,
  UgcListResponse,
  UgcCollaboratorParams,
  UgcMembersResponse,
  UgcBrowseParams,
  UgcBrowseResponse,
  UgcGetManyParams,
  UgcGetManyResponse,
  UgcReportParams,
  UgcCheckTextOptions,
  UgcCheckTextResult,
  UgcCountParams,
  UgcFollowCounts,
  UgcCrossAppBrowseParams,
  UgcCrossAppCreateParams,
  UgcCrossAppUpdateParams,
  UgcVotingApi,
  UgcVoteParams,
  UgcUnvoteParams,
  UgcVoteMutationResult,
  UgcMyVotesResponse,
  UgcVoteListParams,
  UgcVoteWinnersListParams,
  UgcVoteLeaderboardResponse,
  UgcVoteWinnersResponse,
  UgcRecommendationResponse,
  UgcCompositeRecommendationResponse,
} from './UgcApi'
import { checkText } from './checkText'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getCloudRunUrl } from '../config/playground'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'

/**
 * Append a contentType filter to a URLSearchParams. Arrays repeat the key
 * (`?contentType=a&contentType=b`) — Express/Cloud Run parses repeats into
 * `string[]` via the `extended: true` body parser. An empty array adds
 * nothing (matches the server's "empty == no filter" semantics).
 */
function appendContentTypeParam(
  qs: URLSearchParams,
  value: string | string[] | undefined,
): void {
  if (value === undefined) return
  if (Array.isArray(value)) {
    for (const v of value) qs.append('contentType', v)
    return
  }
  qs.set('contentType', value)
}

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined).
 */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {}
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key] = obj[key]
    }
  }
  return result
}

/**
 * Make an authenticated request to Cloud Run.
 */
async function callCloudRun<TResponse>(
  method: string,
  endpoint: string,
  body?: unknown,
): Promise<TResponse> {
  const client = await getFirebaseClient()
  const idToken = await client.getIdTokenOrThrow()
  const baseUrl = getCloudRunUrl()
  const url = `${baseUrl}${endpoint}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  }

  const options: RequestInit = {
    method,
    headers,
  }

  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }

  const gst = await getPlaygroundGameSessionToken()
  if (gst) headers['x-h5-game-session'] = gst

  const response = await fetch(url, options)

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Cloud Run request failed (${response.status})`
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.error || errorJson.message || errorMessage
    } catch {
      if (errorText) {
        errorMessage = errorText
      }
    }
    throw new Error(errorMessage)
  }

  const text = await response.text()
  if (!text) {
    return {} as TResponse  // OK for void-returning methods (delete, report)
  }

  return JSON.parse(text) as TResponse
}

export class HttpUgcApi implements UgcApi {
  private readonly appId: string
  private readonly versionTag?: string

  constructor(appId: string, options?: { versionTag?: string }) {
    this.appId = appId
    this.versionTag = options?.versionTag
  }

  private tagQuery(queryParams: URLSearchParams): void {
    if (this.versionTag) {
      queryParams.set('tagId', this.versionTag)
    }
  }

  private withTag(body: Record<string, unknown>): Record<string, unknown> {
    if (this.versionTag) {
      return { ...body, tagId: this.versionTag }
    }
    return body
  }

  async create(params: UgcCreateParams): Promise<UgcEntry> {
    return callCloudRun<UgcEntry>('POST', `/v1/ugc/${this.appId}/entries`, this.withTag(omitUndefined({
      contentType: params.contentType,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
    })))
  }

  async update(params: UgcUpdateParams): Promise<UgcEntry> {
    return callCloudRun<UgcEntry>('PUT', `/v1/ugc/${this.appId}/entries/${params.id}`, this.withTag(omitUndefined({
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
    })))
  }

  async delete(id: string): Promise<void> {
    await callCloudRun<void>('DELETE', `/v1/ugc/${this.appId}/entries/${id}`)
  }

  async get(id: string): Promise<UgcEntry | null> {
    try {
      return await callCloudRun<UgcEntry>('GET', `/v1/ugc/${this.appId}/entries/${id}`)
    } catch (error: unknown) {
      // Return null for not found errors (case-insensitive, also handles 404 status)
      if (error instanceof Error) {
        const msg = error.message.toLowerCase()
        if (msg.includes('not found') || msg.includes('(404)')) {
          return null
        }
      }
      throw error
    }
  }

  async listMine(params?: UgcListParams): Promise<UgcListResponse> {
    const queryParams = new URLSearchParams()
    this.tagQuery(queryParams)
    appendContentTypeParam(queryParams, params?.contentType)
    if (params?.cursor) queryParams.set('cursor', params.cursor)
    if (params?.limit) queryParams.set('limit', String(params.limit))

    const query = queryParams.toString()
    const endpoint = `/v1/ugc/${this.appId}/mine${query ? `?${query}` : ''}`

    return callCloudRun<UgcListResponse>('GET', endpoint)
  }

  // Collaborators (multi-editor)

  async listShared(params?: UgcListParams): Promise<UgcListResponse> {
    return callCloudRun<UgcListResponse>(
      'POST',
      `/v1/ugc/${this.appId}/shared`,
      this.withTag(omitUndefined({
        contentType: params?.contentType,
        cursor: params?.cursor,
        limit: params?.limit,
      })),
    )
  }

  async addCollaborator(params: UgcCollaboratorParams): Promise<void> {
    await callCloudRun<void>(
      'POST',
      `/v1/ugc/${this.appId}/entries/${params.entryId}/members`,
      this.withTag({ editorId: params.profileId }),
    )
  }

  async removeCollaborator(params: UgcCollaboratorParams): Promise<void> {
    await callCloudRun<void>(
      'DELETE',
      `/v1/ugc/${this.appId}/entries/${params.entryId}/members/${params.profileId}`,
    )
  }

  async listCollaborators(entryId: string): Promise<UgcMembersResponse> {
    return callCloudRun<UgcMembersResponse>(
      'GET',
      `/v1/ugc/${this.appId}/entries/${entryId}/members`,
    )
  }

  async browse(params?: UgcBrowseParams): Promise<UgcBrowseResponse> {
    // Browse uses POST to pass body params (matching the route definition)
    return callCloudRun<UgcBrowseResponse>('POST', `/v1/ugc/${this.appId}/browse`, this.withTag(omitUndefined({
      contentType: params?.contentType,
      authorId: params?.authorId,
      cursor: params?.cursor,
      limit: params?.limit,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      filters: params?.filters,
    })))
  }

  async getMany(params: UgcGetManyParams): Promise<UgcGetManyResponse> {
    return callCloudRun<UgcGetManyResponse>('POST', `/v1/ugc/${this.appId}/get-many`, {
      entryIds: params.entryIds,
    })
  }

  // Phase 2: Engagement methods

  async like(id: string): Promise<{ likeCount: number }> {
    return callCloudRun<{ likeCount: number }>('POST', `/v1/ugc/${this.appId}/entries/${id}/like`)
  }

  async unlike(id: string): Promise<{ likeCount: number }> {
    return callCloudRun<{ likeCount: number }>('DELETE', `/v1/ugc/${this.appId}/entries/${id}/like`)
  }

  async recordUse(id: string): Promise<{ useCount: number }> {
    return callCloudRun<{ useCount: number }>('POST', `/v1/ugc/${this.appId}/entries/${id}/use`)
  }

  async report(params: UgcReportParams): Promise<void> {
    await callCloudRun<void>('POST', `/v1/ugc/${this.appId}/entries/${params.id}/report`, this.withTag(omitUndefined({
      reason: params.reason,
      details: params.details,
      supportEmail: params.supportEmail,
      metadata: params.metadata,
    })))
  }

  // Runs locally — no Cloud Run call.
  async checkTextAsync(text: string, options?: UgcCheckTextOptions): Promise<UgcCheckTextResult> {
    return checkText(text, options)
  }

  async count(params?: UgcCountParams): Promise<{ count: number }> {
    const queryParams = new URLSearchParams()
    this.tagQuery(queryParams)
    // contentType can come from params.contentType (string | string[]) or be
    // mistakenly placed under params.filters.contentType. Honour both, but
    // route through appendContentTypeParam so arrays repeat the key rather
    // than collapsing via String() into a comma-joined garbage value.
    const contentType = params?.contentType ?? (params?.filters?.contentType as string | string[] | undefined)
    appendContentTypeParam(queryParams, contentType)
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (key === 'contentType' || key === 'tagId') continue
        if (value !== undefined) {
          queryParams.set(key, String(value))
        }
      }
    }
    const query = queryParams.toString()
    const endpoint = `/v1/ugc/${this.appId}/count${query ? `?${query}` : ''}`
    return callCloudRun<{ count: number }>('GET', endpoint)
  }

  // Creator following

  async follow(targetProfileId: string): Promise<void> {
    await callCloudRun<void>('POST', `/v1/ugc/${this.appId}/creators/${targetProfileId}/follow`)
  }

  async unfollow(targetProfileId: string): Promise<void> {
    await callCloudRun<void>('DELETE', `/v1/ugc/${this.appId}/creators/${targetProfileId}/follow`)
  }

  async isFollowing(targetProfileId: string): Promise<{ isFollowing: boolean }> {
    return callCloudRun<{ isFollowing: boolean }>('GET', `/v1/ugc/${this.appId}/creators/${targetProfileId}/follow`)
  }

  async getFollowCounts(targetProfileId: string): Promise<UgcFollowCounts> {
    return callCloudRun<UgcFollowCounts>('GET', `/v1/ugc/${this.appId}/creators/${targetProfileId}/follow-counts`)
  }

  // Cross-app UGC

  async crossAppBrowse(params: UgcCrossAppBrowseParams): Promise<UgcBrowseResponse> {
    return callCloudRun<UgcBrowseResponse>('POST', `/v1/ugc/${this.appId}/browse`, this.withTag(omitUndefined({
      targetAppId: params.targetAppId,
      contentType: params.contentType,
      authorId: params.authorId,
      cursor: params.cursor,
      limit: params.limit,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      filters: params.filters,
    })))
  }

  async crossAppGet(targetAppId: string, id: string): Promise<UgcEntry | null> {
    try {
      const queryParams = new URLSearchParams()
      queryParams.set('targetAppId', targetAppId)
      return await callCloudRun<UgcEntry>('GET', `/v1/ugc/${this.appId}/entries/${id}?${queryParams.toString()}`)
    } catch (error: unknown) {
      if (error instanceof Error) {
        const msg = error.message.toLowerCase()
        if (msg.includes('not found') || msg.includes('(404)')) {
          return null
        }
      }
      throw error
    }
  }

  async crossAppGetMany(targetAppId: string, params: UgcGetManyParams): Promise<UgcGetManyResponse> {
    return callCloudRun<UgcGetManyResponse>('POST', `/v1/ugc/${this.appId}/get-many`, {
      targetAppId,
      entryIds: params.entryIds,
    })
  }

  async crossAppCount(targetAppId: string, params?: UgcCountParams): Promise<{ count: number }> {
    const queryParams = new URLSearchParams()
    this.tagQuery(queryParams)
    queryParams.set('targetAppId', targetAppId)
    appendContentTypeParam(queryParams, params?.contentType)
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (key === 'contentType' || key === 'tagId') continue
        if (value !== undefined) {
          queryParams.set(key, String(value))
        }
      }
    }
    const query = queryParams.toString()
    const endpoint = `/v1/ugc/${this.appId}/count${query ? `?${query}` : ''}`
    return callCloudRun<{ count: number }>('GET', endpoint)
  }

  async crossAppCreate(params: UgcCrossAppCreateParams): Promise<UgcEntry> {
    return callCloudRun<UgcEntry>('POST', `/v1/ugc/${this.appId}/entries/cross-app-create`, omitUndefined({
      targetAppId: params.targetAppId,
      contentType: params.contentType,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
      uniqueKey: params.uniqueKey,
      audience: params.audience,
    }))
  }

  async crossAppUpdate(params: UgcCrossAppUpdateParams): Promise<UgcEntry> {
    return callCloudRun<UgcEntry>('PUT', `/v1/ugc/${this.appId}/entries/${params.id}/cross-app-update`, omitUndefined({
      targetAppId: params.targetAppId,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
      uniqueKey: params.uniqueKey,
      audience: params.audience,
    }))
  }

  async crossAppDelete(targetAppId: string, id: string): Promise<void> {
    const queryParams = new URLSearchParams()
    queryParams.set('targetAppId', targetAppId)
    await callCloudRun<void>('DELETE', `/v1/ugc/${this.appId}/entries/${id}/cross-app-delete?${queryParams.toString()}`)
  }

  // Recommendations

  async getUserRecommendation(count: number, filter?: string): Promise<UgcRecommendationResponse> {
    return callCloudRun<UgcRecommendationResponse>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/user`,
      omitUndefined({ count, filter }),
    )
  }

  async getItemRecommendation(ugcId: string, count: number, filter?: string): Promise<UgcRecommendationResponse> {
    return callCloudRun<UgcRecommendationResponse>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/item`,
      omitUndefined({ ugcId, count, filter }),
    )
  }

  async getCompositeRecommendation(count: number, sourceFilter?: string, resultFilter?: string): Promise<UgcCompositeRecommendationResponse> {
    return callCloudRun<UgcCompositeRecommendationResponse>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/composite`,
      omitUndefined({ count, sourceFilter, resultFilter }),
    )
  }

  async getMoreRecommendations(origRecommId: string, count: number): Promise<UgcRecommendationResponse> {
    return callCloudRun<UgcRecommendationResponse>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/next`,
      { origRecommId, count },
    )
  }

  async addDetailView(ugcId: string, recommId?: string): Promise<void> {
    await callCloudRun<void>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/detail-view`,
      omitUndefined({ ugcId, recommId }),
    )
  }

  async addRating(ugcId: string, rating: number, recommId?: string): Promise<void> {
    await callCloudRun<void>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/rating`,
      omitUndefined({ ugcId, rating, recommId }),
    )
  }

  async addCartAddition(ugcId: string, recommId?: string): Promise<void> {
    await callCloudRun<void>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/cart-addition`,
      omitUndefined({ ugcId, recommId }),
    )
  }

  async setViewPortion(ugcId: string, portion: number, recommId?: string): Promise<void> {
    await callCloudRun<void>(
      'POST',
      `/v1/ugc/${this.appId}/recommendations/view-portion`,
      omitUndefined({ ugcId, portion, recommId }),
    )
  }

  // Weekly voting

  readonly voting: UgcVotingApi = {
    vote: async (params: UgcVoteParams): Promise<UgcVoteMutationResult> => {
      return callCloudRun<UgcVoteMutationResult>(
        'POST',
        `/v1/ugc/${this.appId}/voting/vote`,
        omitUndefined({
          entryId: params.entryId,
          count: params.count,
          clientRequestId: params.clientRequestId,
        }),
      )
    },
    unvote: async (params: UgcUnvoteParams): Promise<UgcVoteMutationResult> => {
      return callCloudRun<UgcVoteMutationResult>(
        'POST',
        `/v1/ugc/${this.appId}/voting/unvote`,
        omitUndefined({
          entryId: params.entryId,
          count: params.count,
          clientRequestId: params.clientRequestId,
        }),
      )
    },
    getMyVotes: async (): Promise<UgcMyVotesResponse> => {
      return callCloudRun<UgcMyVotesResponse>('GET', `/v1/ugc/${this.appId}/voting/me`)
    },
    getLeaderboard: async (params?: UgcVoteListParams): Promise<UgcVoteLeaderboardResponse> => {
      const qs = new URLSearchParams()
      if (params?.limit !== undefined) qs.set('limit', String(params.limit))
      if (params?.cursor) qs.set('cursor', params.cursor)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return callCloudRun<UgcVoteLeaderboardResponse>(
        'GET',
        `/v1/ugc/${this.appId}/voting/leaderboard${suffix}`,
      )
    },
    getWinners: async (params?: UgcVoteWinnersListParams): Promise<UgcVoteWinnersResponse> => {
      const qs = new URLSearchParams()
      if (params?.limit !== undefined) qs.set('limit', String(params.limit))
      if (params?.cursor) qs.set('cursor', params.cursor)
      if (params?.topN !== undefined) qs.set('topN', String(params.topN))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return callCloudRun<UgcVoteWinnersResponse>(
        'GET',
        `/v1/ugc/${this.appId}/voting/winners${suffix}`,
      )
    },
  }
}
